// src/axiosIntercept.js
import axios from './services/api';
import { forceLogout } from './context/accountUtils';

// Ensure requests don't hang forever (instance-level)
try {
  if (axios?.defaults) {
    axios.defaults.timeout = 8000; // safe default; adjust if you like
  }
} catch { /* no-op */ }

// Broadcast a lightweight signal for UX, without changing existing flows
function broadcastNetguard(reason, message) {
  try {
    window.dispatchEvent(
      new CustomEvent('netguard:error', {
        detail: { reason, message: message || '' },
      })
    );
  } catch { /* no-op */ }
}

// Helper: classify public auth endpoints
function isAuthEndpoint(url = '') {
  // Accept both absolute and relative; match tail of path
  // /login.php, /signup.php, /verify_email.php, /resend_verify_code.php, /forgot_password.php, /reset_password.php
  return /\/(login|signup|verify_email|resend_verify_code|forgot_password|reset_password)\.php(\?|$)/i.test(url);
}

// Helper: did request carry a bearer token?
function hadBearer(config = {}) {
  const h = config.headers || {};
  const auth = h.Authorization || h.authorization;
  return typeof auth === 'string' && auth.toLowerCase().startsWith('bearer ');
}

// ✅ Helper: expected 404s we don't want to treat as "netguard/server error"
function isExpectedBoardNotFound(status, error, url) {
  if (status !== 404) return false;
  if (error !== 'Board not found') return false;

  // Only suppress for endpoints where this is normal during delete/cache-warm races
  return (
    /\/(getconfig|tiles)\.php(\?|$)/i.test(url)
  );
}

axios.interceptors.response.use(
  res => res,
  err => {
    const status = err?.response?.status;
    const data = err?.response?.data;
    const error = (data && typeof data === 'object') ? data.error : undefined;

    const cfg = err?.config || {};
    const url = String(cfg.url || '');

    const publicAuth = isAuthEndpoint(url);
    const hasBearer = hadBearer(cfg);

    if (isExpectedBoardNotFound(status, error, url)) {
      return Promise.reject(err);
    }

    console.log('status:', status, 'error:', error);
    // --- Auth handling (scoped) ---
    // Multiple login detection remains global (server-driven)
    if (!publicAuth && status === 409 && error === 'multiple_login_detected') {
      forceLogout('multipleusers');
    }
    else if (!publicAuth && status === 404 && error === 'User not found') {
      forceLogout('invalidcredentials');
    }
    // Only force-logout on protected calls failing auth (token bad/expired)
    //else if (!publicAuth && hasBearer && status === 401) {
    else if (!publicAuth && hasBearer && (status === 401 || status === 403)) {
      forceLogout('expired');
    }
    // Otherwise: do NOT force-logout.
    // Auth screens (login/signup/verify/forgot/reset) are allowed to return 4xx without redirecting the app.

    // --- Netguard signal (unchanged behavior) ---
    const reason =
      err?.code === 'ECONNABORTED'
        ? 'timeout'
        : (typeof navigator !== 'undefined' && !navigator.onLine)
        ? 'offline'
        : 'server';

    broadcastNetguard(reason, err?.message);

    return Promise.reject(err);
  }
);
